import { useEffect, type ReactNode } from "react";
import { useAnimate } from "framer-motion";
import { cn } from "../../../lib/cn";
import { useStage, type SceneTone } from "./context";
import { useChoreography, type Timeline } from "./choreography";

export interface SceneProps {
  /** Anchor id (also the pager's key). */
  id: string;
  /** Pager label. */
  label: string;
  tone?: SceneTone;
  /** The entrance, played each time the page settles on this scene (see ./fx). */
  timeline: Timeline;
  className?: string;
  children: ReactNode;
}

/**
 * One full-screen, snap-aligned section of the landing page. Content may
 * outgrow the viewport on small phones; the browser then scrolls within the
 * scene before snapping on to the next.
 */
export function Scene({ id, label, tone = "light", timeline, className, children }: SceneProps) {
  const [scope, animate] = useAnimate<HTMLElement>();
  const { register, arrivedId } = useStage();

  useEffect(() => register({ id, label, tone }, scope.current), [register, id, label, tone, scope]);

  const onFocus = useChoreography(scope, animate, timeline, arrivedId === id);

  return (
    <section
      ref={scope}
      id={id}
      data-scene={id}
      aria-label={label}
      // In-page links focus the scene, so keyboard and screen-reader position
      // follow the glide instead of staying behind on the link.
      tabIndex={-1}
      onFocus={onFocus}
      className={cn(
        "relative isolate flex min-h-dvh snap-start snap-always flex-col overflow-hidden outline-none",
        className,
      )}
    >
      {children}
    </section>
  );
}
